import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DialogProps as MUIDialogProps } from "@mui/material/Dialog";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { keyframes, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import {
  createMRTColumnHelper,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { MaskedTeam } from "work/_shared/masked-fields";
import FeeScheduleLink from "work/fee-schedule-templates/_views/links/fee-schedule-template-link";
import ProposalRequestDraftDialog from "work/proposal-requests/view/proposal-request-draft-dialog";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  HumanReadableProposalStatus,
  ProposalBoxType,
  ProposalOrderField,
  ProposalStatus,
} from "work/proposals/__constants/constants";
import {
  useBulkDeleteProposalsMutation,
  useCreateProposalMutation,
  useGetProposalByIdQuery,
  useGetProposalsQuery,
  useSaveProposalMutation,
  useShareProposalMutation,
} from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  addProposal,
  clearNewProposalBuilder,
  getLastSuccessfulProposalQueryByInterface,
  getOriginalProposalBuilderByProposalId,
  getProposalById,
  getProposalQueryByInterface,
  getProposalsByQuery,
  getUpdatedProposalBuilderByProposalId,
  isProposalLoadIssue,
  ProposalLoadIssue,
  resetBuilderByProposalId,
  resetProposalSessionByProposalId,
  updateProposalInterfaceQuery,
} from "work/proposals/_store/proposals-redux-slice";
import CreatorLink from "work/proposals/_views/_links/creator-link/creator-link";
import ProposalToolbar from "work/proposals/_views/_toolbars/proposal-toolbar";
import ProposalRedlineDialog from "work/proposals/_views/active/active-proposal-dialog";
import ArchivedProposalDialog from "work/proposals/_views/archive/archived-proposal-dialog";
import ProposalDraftDialog from "work/proposals/_views/drafts/draft-proposal-dialog";
import { PageTab } from "work/proposals/_views/proposals-page-tabs";
import {
  clearUnsavedCommentsByProposalId,
  getArePendingComments,
  getAreUnsavedComments,
  publishComments,
  saveComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import { ProposalFieldCategory, ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";

const interfaceName = "proposalsPage";

const ProposalToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ProposalToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const ProposalAlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const columnHelper = createMRTColumnHelper<Proposal | ProposalLoadIssue>();

export default function ProposalsPage() {
  const lastAutoOpenKeyRef = useRef<string | null>(null);

  const { numDialogs, openDialog, closeDialog, updateDialogProps } = useDialog();

  const dialogIsDirtyRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const location = useLocation();

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const proposalId = useMemo(() => (routeParams.id ? new Guid(routeParams.id) : Guid.EMPTY), [routeParams.id]);

  const proposalsQueryParams = useSelector(getProposalQueryByInterface(interfaceName));

  const paginatedProposalsJson = useSelector(getProposalsByQuery(proposalsQueryParams));
  const paginatedProposals = useMemo(() => {
    if (!paginatedProposalsJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const proposalJson of paginatedProposalsJson.data) {
      try {
        if (isProposalLoadIssue(proposalJson)) {
          deserializedData.push(proposalJson);
        } else {
          const proposal = Proposal.fromJSON({
            json: proposalJson,
            currentUser: session.user!,
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", proposalJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in proposalJson ? (Guid.fromJSON(proposalJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedProposalsJson, session.user]);

  const proposalsAPIQuery = useGetProposalsQuery(
    proposalsQueryParams ? { query: proposalsQueryParams, interfaceName } : skipToken,
  );
  const [saveProposalQuery] = useSaveProposalMutation();
  const [createProposalQuery] = useCreateProposalMutation();
  const [shareProposalQuery, shareProposalResult] = useShareProposalMutation();

  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulProposalQueryByInterface(interfaceName)); // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isProposalLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const proposal = Proposal.fromJSON({
            json: item,
            currentUser: session.user!,
            options: { withRedline: false },
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson, session.user]);

  const tableProposals = paginatedProposals ?? lastSuccessfulQueryResponse;

  const proposalByIdQuery = useGetProposalByIdQuery(
    proposalId && !proposalId.isEmpty ? { proposalId: proposalId.value, session } : skipToken,
  );
  const dialogProposalJson = useSelector(getProposalById(proposalId));
  const dialogProposal = useMemo(() => {
    if (!dialogProposalJson || isProposalLoadIssue(dialogProposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: dialogProposalJson,
      currentUser: session.user,
    });
  }, [dialogProposalJson, session.user]);

  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId));
  const originalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: originalBuilderJSON, session });
  }, [originalBuilderJSON, session]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId));
  const updatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: updatedBuilderJSON, session });
  }, [updatedBuilderJSON, session]);

  const updatedBuilderIssues = useMemo(() => {
    return updatedBuilder ? ProposalIssues.fromBuilder(updatedBuilder) : new ProposalIssues();
  }, [updatedBuilder]);
  const updatedBuilderIssuesRef = useRef<ProposalIssues>(updatedBuilderIssues);
  useEffect(() => {
    updatedBuilderIssuesRef.current = updatedBuilderIssues;
  }, [updatedBuilderIssues]);

  const [activeTab, setActiveTab] = useState(PageTab.Active);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  const [activeDialogId, setActiveDialogId] = useState<Guid | undefined>();
  const [activeDialogProps, setActiveDialogProps] = useState<DialogProps>();
  const [isSaving, setIsSaving] = useState(false);

  const comments = useSelector((state: RootState) => state.comments.entries);
  const arePendingComments = useSelector(getArePendingComments(dialogProposal));
  const areUnsavedComments = useSelector(getAreUnsavedComments(dialogProposal));

  const parentPath = new URL(".", globalThis.location.origin + globalThis.location.pathname);

  const dialogProposalRef = useRef(dialogProposal);
  const updatedBuilderRef = useRef(updatedBuilder);
  const arePendingCommentsRef = useRef(arePendingComments);
  const areUnsavedCommentsRef = useRef(areUnsavedComments);

  const [bulkDeleteProposalsQuery, bulkDeleteProposalsResult] = useBulkDeleteProposalsMutation();
  const prevBulkDeleteFailureIdValues = useRef<string[]>([]);
  const bulkDeleteFailureIdValues = useRef<string[]>([]);

  const handleSaveDraft = useCallback(
    async (preventNavigation?: boolean) => {
      prevBulkDeleteFailureIdValues.current = [...bulkDeleteFailureIdValues.current];
      if (!updatedBuilderRef.current) throw new Error("Proposal builder is required to save a proposal.");
      setIsSaving(true);
      try {
        let responseJSON: ProposalJSON;

        if (!proposalId.isEmpty && dialogProposalRef.current) {
          responseJSON = await saveProposalQuery({
            session,
            updatedProposal: dialogProposalRef.current,
            proposalBuilderJSON: updatedBuilderRef.current.toJSON(),
          }).unwrap();
          dispatch(saveComments({ session }));
        } else {
          const unsavedDraftProposal = updatedBuilderRef.current.buildNewDraft();
          responseJSON = await createProposalQuery({
            proposal: unsavedDraftProposal,
            session,
          }).unwrap();
          dispatch(clearNewProposalBuilder());
        }

        if (!responseJSON.id) throw new Error("Saved proposal is missing an ID");
        enqueueSnackbar("Changes saved", { variant: "success" });

        if (responseJSON.id && !preventNavigation && !location.pathname.includes("/proposals/drafts/edit/")) {
          navigate(`/proposals/drafts/edit/${responseJSON.id}`, {
            replace: true,
            state: { exemptFromClose: true },
          });
        }

        return responseJSON.id;
      } finally {
        setIsSaving(false);
      }
    },
    [createProposalQuery, dispatch, location.pathname, navigate, proposalId, saveProposalQuery, session],
  );

  const handleSaveRedlinedProposal = useCallback(
    async (proposal: Proposal) => {
      prevBulkDeleteFailureIdValues.current = [...bulkDeleteFailureIdValues.current];
      if (!proposal?.redline) {
        console.error("No proposal to save");
        return;
      }

      try {
        setIsSaving(true);

        await saveProposalQuery({
          updatedProposal: proposal,
          session,
        }).unwrap();

        dispatch(saveComments({ session }));
        enqueueSnackbar("Changes saved", { variant: "success" });

        if (proposal.areReviewersModified) {
          enqueueSnackbar("Reviewers have been updated/invited", {
            variant: "success",
          });
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to save proposal", { variant: "error" });
      } finally {
        setIsSaving(false);
      }
    },
    [saveProposalQuery, session, dispatch, setIsSaving],
  );

  const handleCloseDraftDialog = useCallback(() => {
    if (proposalId && !proposalId.isEmpty) {
      dispatch(clearUnsavedCommentsByProposalId(proposalId));
      dispatch(
        resetBuilderByProposalId({
          id: proposalId,
          session,
        }),
      );
    } else {
      dispatch(clearNewProposalBuilder());
    }

    setActiveDialogId(undefined);
    setActiveDialogProps(undefined);
    navigate("/proposals/drafts", { replace: true });
  }, [proposalId, dispatch, session, navigate]);

  const handleSaveAndShareChanges = useCallback(
    async (args?: { proposal?: Proposal; confirmShare?: boolean }): Promise<string | null | undefined> => {
      prevBulkDeleteFailureIdValues.current = [...bulkDeleteFailureIdValues.current];
      const { proposal, confirmShare = true } = args ?? {};
      let proposalToSaveAndShare = proposal ?? dialogProposalRef.current;
      if (!proposalToSaveAndShare) {
        proposalToSaveAndShare = updatedBuilderRef.current?.buildNewDraft();
      } else if (!proposalToSaveAndShare.isSubmitted && updatedBuilderRef.current) {
        proposalToSaveAndShare = updatedBuilderRef.current.updateDraftProposal(proposalToSaveAndShare);
      }

      if (!proposalToSaveAndShare) {
        console.error("No proposal to save and share");
        enqueueSnackbar("There was a problem saving and sharing the proposal", {
          variant: "error",
        });
        return;
      }

      if (confirmShare) {
        const response = await confirm({
          title: "Share",
          message: "Are you sure you want to share any pending comments and/or changes for review?",
          okButtonText: "Share",
        });

        if (response === ConfirmResponse.Cancel) return;
      }

      const userCanShare = proposalToSaveAndShare.userCanShare(session.user);
      const proposalIsModified = proposalToSaveAndShare.isModified;

      if (arePendingCommentsRef.current) {
        await dispatch(publishComments({ session })).unwrap();
        enqueueSnackbar("Comments published.", { variant: "info" });
      }
      if (!userCanShare) {
        enqueueSnackbar("You do not have permission to share this proposal", {
          variant: "warning",
        });
        return;
      }
      if (!proposalIsModified) {
        return proposalToSaveAndShare.id?.value;
      }

      const savedProposalJSON = await shareProposalQuery({
        proposal: proposalToSaveAndShare,
        session,
      }).unwrap();
      if (savedProposalJSON) {
        enqueueSnackbar("Proposal changes shared internally.", {
          variant: "success",
        });
      } else {
        console.error("Failed to share proposal changes", shareProposalResult.error);
        enqueueSnackbar("Failed to share proposal changes", {
          variant: "error",
        });
        return;
      }

      return savedProposalJSON?.id;
    },
    [confirm, session, dispatch, shareProposalQuery, shareProposalResult.error],
  );

  const handleCreateDraftProposal = useCallback(() => {
    const draftDialogProps: DialogProps = {
      title: "Create Proposal",
      MuiProps: {
        fullWidth: false,
        maxWidth: "lg",
      } as Partial<MUIDialogProps>,
      contentSxProps: { pb: { xs: 0 } },
      titleStyle: { paddingBottom: 0 },
      resetStack: true,
      onClose: handleCloseDraftDialog,
      isDirtyRef: dialogIsDirtyRef,
      showSaveButton: true,
      disableSave: true,
      onSave: (saveAbleItem?: unknown, preventNavigation?: boolean) => {
        if (saveAbleItem && saveAbleItem instanceof ProposalBuilder) {
          return handleSaveDraft(preventNavigation);
        }
        throw new Error("Invalid proposal builder");
      },
      component: <ProposalDraftDialog isSaving={isSaving} onSaveAndShareChanges={handleSaveAndShareChanges} />,
    };

    setActiveDialogProps(draftDialogProps);
    setActiveDialogId(openDialog(draftDialogProps));
  }, [handleCloseDraftDialog, handleSaveDraft, isSaving, handleSaveAndShareChanges, openDialog, dialogIsDirtyRef]);

  const handleOpenDraftProposalDialog = useCallback(
    (title: string, proposalId?: Guid, disableEditing?: boolean) => {
      if (!proposalId) {
        console.error("Proposal ID is required to edit a draft proposal.");
        return;
      }

      const draftDialogProps: DialogProps = {
        title: title,
        MuiProps: { fullWidth: false, maxWidth: "lg" },
        contentSxProps: { pb: { xs: 0 } },
        titleStyle: { paddingBottom: 0 },
        resetStack: true,
        onClose: handleCloseDraftDialog,
        isDirtyRef: dialogIsDirtyRef,
        showSaveButton: true,
        disableSave: true,
        onSave: async () => {
          if (!updatedBuilder) {
            console.error("Nothing to save.");
            return;
          }

          await handleSaveDraft(Boolean(dialogProposal?.createdDate));
        },
        component: (
          <ProposalDraftDialog
            proposalId={proposalId}
            isSaving={isSaving}
            onSaveAndShareChanges={handleSaveAndShareChanges}
            disableEditing={disableEditing}
          />
        ),
      };
      setActiveDialogProps(draftDialogProps);
      setActiveDialogId(openDialog(draftDialogProps));
    },
    [
      handleCloseDraftDialog,
      updatedBuilder,
      handleSaveDraft,
      dialogProposal?.createdDate,
      isSaving,
      handleSaveAndShareChanges,
      openDialog,
      dialogIsDirtyRef,
    ],
  );

  const handleOpenActiveProposalDialog = useCallback(
    (title: string, proposalId: Guid, readOnly?: boolean) => {
      prevBulkDeleteFailureIdValues.current = [...bulkDeleteFailureIdValues.current];
      const parentPath = new URL("..", globalThis.location.origin + globalThis.location.pathname);
      const handleClose = () => {
        dispatch(
          resetProposalSessionByProposalId({
            id: proposalId,
          }),
        );
        dispatch(clearUnsavedCommentsByProposalId(proposalId));
        closeDialog();
        navigate(parentPath.pathname ?? "/proposals/active");
      };

      const redlineDialogProps: DialogProps = {
        title: title,
        titleStyle: { paddingBottom: 0 },
        MuiProps: { fullWidth: false, maxWidth: "xl" },
        contentSxProps: { pb: { xs: 0 } },
        resetStack: true,
        onClose: handleClose,
        showSaveButton: true,
        disableSave: true,
        onSave: async (saveAbleItem?: unknown, _preventNavigation?: boolean) => {
          if (saveAbleItem && saveAbleItem instanceof Proposal) {
            return handleSaveRedlinedProposal(saveAbleItem);
          }
          throw new Error("Invalid proposal to save");
        },
        component: (
          <ProposalRedlineDialog
            proposalId={proposalId}
            isSaving={isSaving}
            disableEditing={readOnly ?? false}
            onSaveAndShareChanges={handleSaveAndShareChanges}
          />
        ),
      };

      setActiveDialogProps(redlineDialogProps);
      setActiveDialogId(openDialog(redlineDialogProps));
    },
    [closeDialog, dispatch, handleSaveAndShareChanges, handleSaveRedlinedProposal, isSaving, navigate, openDialog],
  );

  const redirectForProposalIfNecessary = useCallback(
    (action: string, reviewer: EntityMember | undefined, pageTab: PageTab) => {
      if (!dialogProposal || !routeParams || (proposalId.isEmpty && routeParams.action === "create")) return;

      // Helper: redirect to superseded proposal
      const redirectIfSuperseded = () => {
        if (dialogProposal.supersededById) {
          navigate(`/proposals/${pageTab}/${action}/${dialogProposal.supersededById.value}`, {
            replace: true,
            state: {
              previousProposalId: dialogProposal.id?.value,
              newProposalId: dialogProposal.supersededById.value,
              exemptFromClose: true,
            },
          });
          return true;
        }
        return false;
      };

      // Helper: redirect to work agreement if hired
      const redirectIfHired = () => {
        if (dialogProposal.isHired && activeTab !== PageTab.Archived) {
          navigate(`/work/active/view/${dialogProposal.workAgreementId?.value}`, {
            replace: true,
            state: { exemptFromClose: true },
          });
          return true;
        }
        return false;
      };

      // Helper: redirect to active proposals if submitted and editing
      const redirectIfSubmittedEdit = () => {
        if (action === "edit" && dialogProposal.isSubmitted) {
          navigate("/proposals/active", { replace: true, state: { exemptFromClose: true } });
          return true;
        }
        return false;
      };

      if (redirectIfSuperseded()) return;
      if (redirectIfHired()) return;
      if (redirectIfSubmittedEdit()) return;

      let redirectAction: string | undefined = undefined;
      if (!dialogProposal.isArchived && reviewer && action !== "review") {
        redirectAction = "review";
      }
      if (dialogProposal.isArchived && action !== "view") {
        redirectAction = "view";
      }

      let redirectPageTab: PageTab | undefined = undefined;
      if (pageTab === PageTab.Drafts && dialogProposal.isSubmitted) {
        redirectPageTab = PageTab.Active;
      }
      if (pageTab === PageTab.Active && !dialogProposal.isSubmitted) {
        redirectPageTab = PageTab.Drafts;
      }
      if (dialogProposal.isArchived && pageTab !== PageTab.Archived) {
        redirectPageTab = PageTab.Archived;
      }

      if (redirectAction || redirectPageTab) {
        const newPath = `/proposals/${redirectPageTab ?? pageTab}/${redirectAction ?? action}/${proposalId}`;
        navigate(newPath, { replace: true });
      }
    },
    [dialogProposal, routeParams, proposalId, activeTab, navigate],
  );

  const handleOpenArchivedProposalDialog = useCallback(
    (proposalId: Guid) => {
      openDialog({
        title: "Archived Proposal",
        MuiProps: { fullWidth: false, maxWidth: "lg" },
        contentSxProps: { pb: { xs: 0 } },
        titleStyle: { paddingBottom: 0 },
        onClose: () => {
          navigate("/proposals/archived");
        },
        component: <ArchivedProposalDialog navigate={navigate} proposalId={proposalId} />,
      });
    },
    [navigate, openDialog],
  );

  const handleRequestProposal = useCallback(async () => {
    openDialog({
      title: "Request Proposal",
      resetStack: true,
      component: (
        <ProposalRequestDraftDialog
          onProposalSubmitted={(submittedProposal: Proposal) => {
            navigate(`/proposals/active/revise/${submittedProposal.id}`, {
              replace: true,
            });
            dispatch(addProposal(submittedProposal));
            closeDialog();
          }}
          onCancel={() => {
            navigate(parentPath.pathname);
            closeDialog();
          }}
        />
      ),
    });
  }, [closeDialog, dispatch, navigate, openDialog, parentPath.pathname]);

  const openProposalDialog = useCallback(
    (action: string, _tab: ProposalFieldCategoryType) => {
      if (dialogProposal?.supersededById && activeTab !== PageTab.Archived) {
        navigate(`/proposals/active/revise/${dialogProposal.supersededById.value}`);
        return;
      }

      if (!session.user || dialogProposal?.supersededById || !action) {
        console.warn("Unable to open proposal dialog. Missing required parameters.");
        return;
      }
      if (numDialogs > 0 && !location.state?.exemptFromClose) {
        return;
      }
      switch (action) {
        case "create":
          handleCreateDraftProposal();
          break;
        case "request":
          handleRequestProposal();
          break;
        case "edit":
          handleOpenDraftProposalDialog("Edit Draft Proposal", proposalId);
          break;
        case "review":
          if (proposalId && dialogProposal?.isSubmitted) {
            handleOpenActiveProposalDialog("Review Proposal", proposalId);
          } else if (dialogProposal) {
            handleOpenDraftProposalDialog("Review Draft Proposal", proposalId);
          }
          break;
        case "revise":
          if (proposalId) {
            handleOpenActiveProposalDialog("Revise Proposal", proposalId);
          }
          break;
        default:
          if (dialogProposal?.isArchived && proposalId) {
            handleOpenArchivedProposalDialog(proposalId);
          } else if (proposalId && dialogProposal?.isSubmitted) {
            handleOpenActiveProposalDialog("View Proposal", proposalId, true);
          } else if (dialogProposal) {
            handleOpenDraftProposalDialog("View Draft Proposal", proposalId, true);
          }
          break;
      }
    },
    [
      dialogProposal,
      activeTab,
      session.user,
      numDialogs,
      location.state?.exemptFromClose,
      navigate,
      handleCreateDraftProposal,
      handleRequestProposal,
      handleOpenDraftProposalDialog,
      proposalId,
      handleOpenActiveProposalDialog,
      handleOpenArchivedProposalDialog,
    ],
  );

  const onSaveDraftFromDialog = useCallback(
    (_?: unknown, preventNavigation?: boolean) => {
      const shouldPreventNav = preventNavigation ?? Boolean(dialogProposalRef.current?.createdDate);
      return handleSaveDraft(shouldPreventNav);
    },
    [handleSaveDraft],
  );

  useEffect(() => {
    dialogProposalRef.current = dialogProposal;
    updatedBuilderRef.current = updatedBuilder;
    arePendingCommentsRef.current = arePendingComments;
    areUnsavedCommentsRef.current = areUnsavedComments;
  }, [dialogProposal, updatedBuilder, areUnsavedComments, arePendingComments, comments]);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!session?.user?.id || dialogProposal?.isSubmitted || dialogProposal?.supersedes) {
      return;
    }
    let isDirty = areUnsavedComments;
    if (!dialogProposal?.isSubmitted) {
      isDirty = isDirty || Boolean(updatedBuilder?.isDifferentFrom(originalBuilder));
    }
    const disableSave = !isDirty || !updatedBuilderIssues.canSave;
    let saveButtonTooltip = "Save changes (just for yourself)";
    if (!isDirty) {
      saveButtonTooltip = "No changes to save";
    } else if (!updatedBuilderIssues.canSave) {
      saveButtonTooltip = "Fill out all required fields to save";
    }

    dialogIsDirtyRef.current = isDirty;
    setActiveDialogProps((prev) => {
      if (!prev) return prev;

      const next: DialogProps = {
        ...prev,
        disableSave,
        saveButtonTooltip,
        isDirty: isDirty,
        isDirtyRef: dialogIsDirtyRef,
        onSave: onSaveDraftFromDialog,
      };

      const changed =
        next.disableSave !== prev.disableSave ||
        next.saveButtonTooltip !== prev.saveButtonTooltip ||
        next.isDirty !== prev.isDirty ||
        next.isDirtyRef?.current !== prev.isDirtyRef?.current ||
        next.onSave !== prev.onSave;

      return changed ? next : prev;
    });
  }, [
    updatedBuilder,
    session?.user?.id,
    dialogProposal?.isSubmitted,
    dialogProposal?.supersedes,
    areUnsavedComments,
    updatedBuilderIssues,
    originalBuilder,
    onSaveDraftFromDialog,
    dialogIsDirtyRef,
  ]);

  useEffect(() => {
    if (!activeDialogId || !activeDialogProps || !dialogProposal?.id) return;

    const isModified = Boolean(
      areUnsavedComments || updatedBuilder?.isDifferentFrom(originalBuilder) || dialogProposal?.isModified,
    );
    dialogIsDirtyRef.current = isModified;

    const disableSave = !isModified || !updatedBuilderIssues.canSave;

    const nextDialogProps: DialogProps = {
      ...activeDialogProps,
      saveButtonTooltip: isModified ? "Save changes (just for yourself)" : "No changes to save",
      isDirtyRef: dialogIsDirtyRef,
      disableSave,
    };

    const changed =
      nextDialogProps.disableSave !== activeDialogProps.disableSave ||
      nextDialogProps.saveButtonTooltip !== activeDialogProps.saveButtonTooltip ||
      nextDialogProps.isDirtyRef?.current !== activeDialogProps.isDirtyRef?.current;

    if (!changed) return;

    setActiveDialogProps(nextDialogProps);
  }, [
    dialogProposal?.redline?.sessionHistory.length,
    areUnsavedComments,
    dialogProposal,
    activeDialogId,
    activeDialogProps,
    updatedBuilder,
    originalBuilder,
    updatedBuilderIssues.canSave,
  ]);

  useEffect(() => {
    if (!activeDialogId || !activeDialogProps) return;
    updateDialogProps(activeDialogId, activeDialogProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDialogId, activeDialogProps]);

  useEffect(() => {
    if (!routeParams) return;
    if (routeParams.pageTab) {
      setActiveTab(routeParams.pageTab as PageTab);
    } else {
      navigate(`/proposals/${PageTab.Active}`);
    }
    lastAutoOpenKeyRef.current = null;
  }, [navigate, routeParams, routeParams.pageTab]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    let boxType = searchParams.get("boxType") as ProposalBoxType | undefined;
    if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Active) boxType = ProposalBoxType.AllActive;
    else if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Drafts) boxType = ProposalBoxType.Draft;
    else if (!boxType && (routeParams.pageTab ?? activeTab) === PageTab.Archived) boxType = ProposalBoxType.Archive;

    const filterParameters = ProposalFilterParameters.fromSearchParams(searchParams, session, boxType);
    const orderParameters = ProposalOrderParameters.fromSearchParams(searchParams);

    const newQuery = new ProposalQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (proposalsQueryParams && newQuery.isEqualTo(proposalsQueryParams)) return;
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    routeParams.pageTab,
    activeTab,
    session.user?.legalEntityId.value,
    session,
    searchParams,
    proposalsQueryParams,
    dispatch,
  ]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value as string | undefined;
    if (statusFilter) {
      newSearchParams.set("status", statusFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
    if (descriptionFilter) {
      newSearchParams.set("description", descriptionFilter);
    }

    const creatorFilter = columnFilters.find((filter) => filter.id === "creator")?.value as string | undefined;
    if (creatorFilter) {
      newSearchParams.set("creator", creatorFilter);
    }

    const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
    if (negotiableFilter !== undefined) {
      newSearchParams.set("negotiable", negotiableFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(ProposalOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex;
    if (paginationPageIndex !== undefined) newSearchParams.set("page", (paginationPageIndex + 1).toString());

    const paginationPageSize = pagination.pageSize;
    if (paginationPageSize !== undefined) newSearchParams.set("pageSize", paginationPageSize.toString());

    if (newSearchParams.toString() === searchParams.toString()) return;

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, searchParams, setSearchParams]);

  useEffect(() => {
    if (proposalsAPIQuery.isError) {
      enqueueSnackbar("Error loading proposal table", { variant: "error" });
    }
  }, [proposalsAPIQuery.isError]);

  useEffect(() => {
    if (
      proposalId.isEmpty ||
      dialogProposal !== null ||
      !routeParams ||
      proposalByIdQuery.isLoading ||
      routeParams.action === "create"
    )
      return;
    if (routeParams.action === "review" && routeParams.id) {
      enqueueSnackbar("You are no longer a reviewer on this proposal", {
        variant: "warning",
      });
    } else if (routeParams.id) {
      enqueueSnackbar("Proposal not found", { variant: "warning" });
    }
    navigate(`/proposals/${activeTab}`);
  }, [activeTab, dialogProposal, navigate, proposalByIdQuery.isLoading, proposalId.isEmpty, routeParams]);

  useEffect(() => {
    if (session.user?.id && dialogProposal?.createdDate) {
      const pageTab = routeParams.pageTab as PageTab;
      const action = routeParams.action as string;
      const reviewer = dialogProposal.getReviewer(session.user.id);

      redirectForProposalIfNecessary(action, reviewer?.entityMember, pageTab);
    }
  }, [dialogProposal, routeParams.pageTab, routeParams.action, session.user?.id, redirectForProposalIfNecessary]);

  useEffect(() => {
    if (!session.user || proposalsAPIQuery.isLoading || (!proposalId.isEmpty && !dialogProposal)) return;

    const action = routeParams.action as string | undefined;
    const dialogTabParam = routeParams.dialogTab as string | undefined;
    const idParam = routeParams.id as string | undefined;
    const pageTabParam = routeParams.pageTab as string | undefined;

    if (!action || !pageTabParam) return;

    // only auto-open once per route "signature"
    const key = `${pageTabParam ?? ""}:${action}:${idParam ?? ""}:${dialogTabParam}`;
    if (lastAutoOpenKeyRef.current === key) return;

    // only auto-open when there's not already a dialog open
    if (numDialogs > 0 && !location.state?.exemptFromClose) return;

    lastAutoOpenKeyRef.current = key;

    const tab = ProposalFieldCategory[dialogTabParam as keyof typeof ProposalFieldCategory];
    openProposalDialog(action, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams,
    proposalsAPIQuery.isLoading,
    routeParams.action,
    routeParams.dialogTab,
    routeParams.id,
    routeParams.pageTab,
    session.user,
  ]);

  // useEffect(() => {
  //   setRowSelection({});
  // }, [activeTab]);

  const handleBulkDraftProposalDelete = useCallback(async () => {
    prevBulkDeleteFailureIdValues.current = [...bulkDeleteFailureIdValues.current];
    const selectedRowIdValues = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIdValues.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const failedDeletionsResponse: Array<string> =
        (await bulkDeleteProposalsQuery({
          ids: selectedRowIdValues,
        }).unwrap()) ?? [];
      if (failedDeletionsResponse.length > 0) {
        bulkDeleteFailureIdValues.current = _.uniq(bulkDeleteFailureIdValues.current.concat(failedDeletionsResponse));
      }

      if (failedDeletionsResponse.length === selectedRowIdValues.length) {
        const error = bulkDeleteProposalsResult.error;
        console.error(error);
        enqueueSnackbar("Failed to delete proposals", { variant: "error" });
        setRowSelection({});
        return;
      }

      if (failedDeletionsResponse.length > 0) {
        enqueueSnackbar(`${failedDeletionsResponse.length} proposals could not be deleted`, {
          variant: "warning",
        });
      } else {
        enqueueSnackbar(`${selectedRowIdValues.length} Proposals deleted successfully`, { variant: "success" });
      }
      setRowSelection({});
      if (proposalsQueryParams) {
        dispatch(
          updateProposalInterfaceQuery({
            interfaceName: interfaceName,
            query: proposalsQueryParams,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete proposals", { variant: "error" });
    }
  }, [
    bulkDeleteProposalsQuery,
    bulkDeleteProposalsResult.error,
    confirm,
    dispatch,
    proposalsQueryParams,
    rowSelection,
  ]);

  const renderStatusColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue): ReactNode => {
      if (isProposalLoadIssue(proposal)) {
        return (
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "4000px",
                  whiteSpace: "normal",
                },
              },
            }}
            title={`Our staff has been notified. Reference id: ${proposal.id.value}`}
          >
            <span>Error Loading Proposal</span>
          </Tooltip>
        );
      }

      const userId = session.user?.id;
      let proposalStatus: string = ProposalStatus[proposal.status]?.toString();
      let displayStatus: string = HumanReadableProposalStatus[proposalStatus as keyof typeof ProposalStatus];
      const isClient =
        MaskedTeam.isPresentNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId.isEqualTo(userId);
      const isTeamLeader =
        MaskedTeam.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam?.leader?.userId.isEqualTo(userId);
      const isVendor = proposal.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(userId));

      if (
        (proposal.status === ProposalStatus.AwaitingApprovalByClient && isClient) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByTeamLeader && isTeamLeader) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
      )
        displayStatus = "Awaiting Your Approval";

      // If the proposal is awaiting review by the user
      if (
        proposal.clientReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        ) ||
        proposal.vendorReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        )
      ) {
        displayStatus = "Awaiting Your Review";
      }
      if (proposal.id?.value && bulkDeleteFailureIdValues.current.includes(proposal.id.value)) {
        return (
          <>
            <span>{displayStatus}</span>
            <br />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "0.7rem",
                color: "error.main",
              }}
            >
              Cannot delete this proposal
            </Box>
          </>
        );
      }

      return displayStatus;
    },
    [session.user?.id],
  );

  const renderCreatorColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal) || !proposal.creator) return <Icon>remove</Icon>;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) {
        return "You";
      }
      return <CreatorLink creator={proposal.creator} />;
    },
    [session.user?.id],
  );

  const renderTeamColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal) || !proposal.vendorTeam) return <Icon>remove</Icon>;
      return <VendorTeamLink proposal={proposal} session={session} />;
    },
    [session],
  );

  const renderFeeScheduleColumn = useCallback((proposal: Proposal | ProposalLoadIssue) => {
    if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return <Icon>remove</Icon>;
    return <FeeScheduleLink proposal={proposal} />;
  }, []);

  const statusFilterOptions = useMemo(
    () =>
      Object.entries(HumanReadableProposalStatus).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        Cell: ({ row }) => renderStatusColumn(row.original),
      }),
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: NameCell,
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        Cell: DescriptionCell,
      }),
      columnHelper.display({
        header: "Creator",
        id: "creator",
        Cell: ({ row }) => renderCreatorColumn(row.original),
      }),
      columnHelper.display({
        header: "Negotiable",
        id: "negotiable",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" },
        ],
        Cell: NegotiableCell,
      }),
      columnHelper.display({
        header: "Team",
        id: "team",
        size: 50,
        Cell: ({ row }) => renderTeamColumn(row.original),
      }),
      columnHelper.display({
        header: "Fee Schedule",
        id: "feeSchedule",
        size: 50,
        Cell: ({ row }) => renderFeeScheduleColumn(row.original),
      }),
      columnHelper.display({
        header: "Response Due By",
        id: "responseDueBy",
        size: 50,
        Cell: ResponseDueByCell,
      }),
      columnHelper.display({
        header: "Start Date",
        id: "startDate",
        size: 50,
        Cell: StartDateCell,
      }),
      columnHelper.display({
        header: "End Date",
        id: "endDate",
        size: 50,
        Cell: EndDateCell,
      }),
      columnHelper.display({
        header: "Hire Date",
        id: "hireDate",
        size: 50,
        Cell: HireDateCell,
      }),
      columnHelper.display({
        header: "Archive Date",
        id: "archiveDate",
        size: 50,
        Cell: ArchiveDateCell,
      }),
    ],
    [statusFilterOptions, renderStatusColumn, renderCreatorColumn, renderTeamColumn, renderFeeScheduleColumn],
  );

  const openProposal = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal)) {
        console.warn("Cannot open proposal with loading issues.");
        return;
      }
      if (!proposal.id) {
        console.error("Proposal ID is required");
        return;
      }
      const editable = proposal.availableActions.Edit.some((userId) => userId.isEqualTo(session.user?.id));
      const revisable = proposal.availableActions.Revise.some((userId) => userId.isEqualTo(session.user?.id));
      const reviewable = proposal.availableActions.Review.some((userId) => userId.isEqualTo(session.user?.id));
      const submittable = proposal.availableActions.Submit.some((userId) => userId.isEqualTo(session.user?.id));

      let tab: PageTab = activeTab;
      let action: "view" | "edit" | "revise" | "review" = "view";

      if (editable && !revisable) {
        tab = PageTab.Drafts;
        action = "edit";
      }
      if (revisable) {
        if (submittable) {
          tab = PageTab.Drafts;
        } else {
          tab = PageTab.Active;
        }
        action = "revise";
      }
      if (reviewable) {
        action = "review";
      }

      navigate(`/proposals/${tab}/${action}/${proposal.id}`);
    },
    [activeTab, navigate, session.user?.id],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => {
      return <ProposalToolbar table={table} activeTab={activeTab} />;
    },
    [activeTab],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ProposalToolbarButtonsContainer>
            <ProposalToolbarButtons>
              <ToggleGlobalFilterButton table={props.table} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleDensePaddingButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ProposalToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              onClick={() => {
                if (routeParams.action === "create") {
                  handleCreateDraftProposal();
                } else {
                  navigate("/proposals/drafts/create");
                }
              }}
            >
              Create Proposal
            </Button>
          </ProposalToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/proposals/drafts/create")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    [isMediumDisplaySize, navigate, routeParams.action, handleCreateDraftProposal],
  );

  const failedOperationKeyFrame = keyframes`
    0% { background-color: rgba(255, 100, 100, 0.1); }
    50% { background-color: rgba(255, 100, 100, 0.3); }
    100% { background-color: rgba(255, 100, 100, 0.1); }
  `;
  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => ({
      onClick: () => openProposal(row.original),
      sx: {
        backgroundColor: isProposalLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: isProposalLoadIssue(row.original) ? "default" : "pointer",
        animation:
          row.original.id &&
          !prevBulkDeleteFailureIdValues.current.includes(row.original.id.value) &&
          bulkDeleteFailureIdValues.current.includes(row.original.id.value)
            ? `${failedOperationKeyFrame} 1s ease-in-out`
            : "none",
        animationIterationCount:
          row.original.id && bulkDeleteFailureIdValues.current.includes(row.original.id.value) ? "3" : "0",
      },
    }),
    [failedOperationKeyFrame, openProposal],
  );

  const muiTableContainerProps = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => ({
      sx: {
        height:
          `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px -` +
          ` ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    [],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (proposalsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    return {
      action: (
        <ProposalAlertActions>
          <Button color="primary" startIcon={<DeleteIcon />} onClick={handleBulkDraftProposalDelete}>
            Delete
          </Button>
        </ProposalAlertActions>
      ),
    };
  }, [proposalsAPIQuery.isError, handleBulkDraftProposalDelete]);

  const tableOptions = useMemo<MRT_TableOptions<Proposal | ProposalLoadIssue>>(() => {
    return {
      columns,
      data: tableProposals ?? [],
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      enableRowSelection:
        activeTab === PageTab.Drafts
          ? (row) => !!row.original.id && !bulkDeleteFailureIdValues.current.includes(row.original.id.value)
          : false,
      muiSelectCheckboxProps: ({ row }) => ({
        onClick: (event) => {
          event.stopPropagation();
          if (row.original.id && bulkDeleteFailureIdValues.current.includes(row.original.id.value)) {
            event.preventDefault();
          }
        },
        title:
          row.original.id && bulkDeleteFailureIdValues.current.includes(row.original.id.value)
            ? "Cannot delete this proposal"
            : undefined,
      }),
      enableRowActions: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      enableColumnPinning: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
      },
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps,
      muiTablePaperProps: {
        sx: {
          height: "100%",
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableProposals?.length ?? 0,
      pageCount: Math.ceil((tableProposals?.length ?? 0) / pagination.pageSize),
      enablePagination: true,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      state: {
        columnVisibility: {
          hireDate: activeTab === PageTab.Archived,
          archiveDate: activeTab === PageTab.Archived,
        },
        columnFilters,
        globalFilter,
        isLoading: proposalsAPIQuery.isFetching,
        pagination,
        showAlertBanner: Boolean(proposalsAPIQuery.isError),
        showProgressBars: proposalsAPIQuery.isFetching,
        sorting,
        rowSelection,
        showSkeletons: proposalsAPIQuery.isLoading,
      },
    };
  }, [
    columns,
    tableProposals,
    activeTab,
    muiTableBodyRowProps,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    muiTableContainerProps,
    muiToolbarAlertBannerProps,
    pagination,
    columnFilters,
    globalFilter,
    proposalsAPIQuery.isFetching,
    proposalsAPIQuery.isLoading,
    proposalsAPIQuery.isError,
    sorting,
    rowSelection,
  ]);

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  return <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} />;
}

const NameCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  const value = !isProposalLoadIssue(row.original) && row.original.name?.value;
  return value ? <RowTitle>{value}</RowTitle> : <Icon>remove</Icon>;
};
const DescriptionCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  const value = !isProposalLoadIssue(row.original) && row.original.description?.value;
  return value ? <Typography variant="body2">{value}</Typography> : <Icon>remove</Icon>;
};
const NegotiableCell = ({ row }: { row: { original: Proposal | ProposalLoadIssue } }) => {
  let value: boolean | undefined;
  if (!isProposalLoadIssue(row.original)) {
    value = row.original.negotiable?.value;
  }
  if (value === undefined) {
    return <Icon>remove</Icon>;
  }
  return value ? <CheckIcon /> : <CloseIcon />;
};

function ResponseDueByCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.responseDueBy;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function StartDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row }: { row: { original: Proposal | ProposalLoadIssue } }) {
  const value = !isProposalLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}
